import type {
  Mistake,
  MistakeReviewItem,
  NextDayAdjustment,
  NextDayAdjustmentInput,
  PracticeAttempt,
  ReviewResultStatus,
  WeaknessCluster,
} from "./models";
import { isoDateSchema, practiceAttemptSchema } from "./schemas";

export interface PracticeMetrics {
  effectiveQuestionCount: number;
  correctRate: number;
  understoodRate: number | null;
  averageSecondsPerQuestion: number | null;
  scoreEligible: boolean;
  band: "red" | "orange" | "first_pass" | "stable";
}

export function calculatePracticeMetrics(input: PracticeAttempt): PracticeMetrics {
  const attempt = practiceAttemptSchema.parse(input);
  const effectiveQuestionCount = attempt.rawQuestionCount - attempt.duplicateQuestionCount;
  const correctRate = attempt.correctCount / effectiveQuestionCount;
  const understoodRate = attempt.evidenceDetailStatus === "quick"
    ? null
    : attempt.understoodQuestionCount / effectiveQuestionCount;

  return {
    effectiveQuestionCount,
    correctRate,
    understoodRate,
    averageSecondsPerQuestion: attempt.elapsedMinutes === null
      ? null
      : (attempt.elapsedMinutes * 60) / effectiveQuestionCount,
    scoreEligible: attempt.attemptKind === "first" && !attempt.lookedUpDuringAttempt,
    band: rateBand(correctRate),
  };
}

export function rateBand(rate: number): PracticeMetrics["band"] {
  if (rate < 0 || rate > 1) throw new RangeError("正确率必须在 0—1 之间");
  if (rate < 0.5) return "red";
  if (rate < 0.7) return "orange";
  if (rate < 0.8) return "first_pass";
  return "stable";
}

function addCalendarDays(date: string, days: number): string {
  const validDate = isoDateSchema.parse(date);
  const [yearText, monthText, dayText] = validDate.split("-");
  if (!yearText || !monthText || !dayText) throw new RangeError("日期必须为 YYYY-MM-DD");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const value = new Date(Date.UTC(year, month - 1, day));
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function buildD1D3D7Schedule(createdDate: string): MistakeReviewItem[] {
  return [
    { interval: "D1", dueDate: addCalendarDays(createdDate, 1), status: "pending" },
    { interval: "D3", dueDate: addCalendarDays(createdDate, 3), status: "pending" },
    { interval: "D7", dueDate: addCalendarDays(createdDate, 7), status: "pending" },
  ];
}

export function dueMistakes(mistakes: Mistake[], onDate: string): Mistake[] {
  return mistakes.filter(
    (mistake) =>
      mistake.status !== "temporarily_mastered" &&
      isMistakeReadyForScheduling(mistake) &&
      mistake.reviews.some((review) => review.status === "pending" && review.dueDate <= onDate),
  );
}

export function completeDueMistakeReview(
  mistake: Mistake,
  onDate: string,
  result: Extract<ReviewResultStatus, "correct" | "incorrect">,
  completedAt: string,
): Mistake {
  if (!isMistakeReadyForScheduling(mistake)) {
    throw new RangeError("错题尚未补全正确结论、依据和防错规则，不能进入回炉");
  }
  const validDate = isoDateSchema.parse(onDate);
  const reviewIndex = mistake.reviews.findIndex(
    (review) => review.status === "pending" && review.dueDate <= validDate,
  );
  if (reviewIndex < 0) throw new RangeError("当前没有可完成的到期回炉任务");

  const reviews = mistake.reviews.map((review, index) => index === reviewIndex
    ? {
        ...review,
        status: result,
        completedAt,
        note: result === "correct" ? "闭卷重做正确" : "闭卷重做仍未掌握",
      }
    : review);
  const allReviewsCompleted = reviews.every((review) => review.status !== "pending");

  return {
    ...mistake,
    reviews,
    status: result === "incorrect"
      ? "reactivated"
      : allReviewsCompleted
        ? "temporarily_mastered"
        : "active",
  };
}

export interface BuildWeaknessClustersInput {
  practiceAttempts: readonly PracticeAttempt[];
  mistakes: readonly Mistake[];
  masteryAssessments?: readonly WeaknessMasteryAssessment[];
}

export interface WeaknessMasteryAssessment {
  eventId: string;
  subject: WeaknessCluster["subject"];
  label: string;
  masteryGrade: NonNullable<WeaknessCluster["masteryGrade"]>;
  createdAt?: string;
}

interface WeaknessClusterAccumulator {
  subject: WeaknessCluster["subject"];
  label: string;
  mistakeCount: number;
  attempts: Array<{
    rate: number;
    timestamp?: number;
  }>;
  masteryAssessments: Array<{
    grade: NonNullable<WeaknessCluster["masteryGrade"]>;
    timestamp?: number;
  }>;
}

function normalizeClusterLabel(label: string): string {
  return label.normalize("NFKC").trim().replace(/\s+/g, " ");
}

/**
 * 粗录错题仅供晚间补全，不得提前进入正式弱点簇或到期回炉排课。
 * 同时识别旧版占位文案，避免升级后既有草稿污染次日计划。
 */
export function isMistakeReadyForScheduling(mistake: Mistake): boolean {
  return mistake.detailStatus !== "draft"
    && mistake.learnerAnswer !== "待晚间补充"
    && mistake.correctConclusion !== "待晚间复盘"
    && mistake.preventionRule !== "待晚间复盘后补充";
}

/**
 * 只用可归入具体 topic 的正式首次未查资料练习计算知识簇成绩。
 * 章节总成绩、混合成绩、重做和查资料成绩均不得伪装成知识簇正确率。
 */
export function buildWeaknessClusters(input: BuildWeaknessClustersInput): WeaknessCluster[] {
  const clusters = new Map<string, WeaknessClusterAccumulator>();
  const getCluster = (subject: WeaknessCluster["subject"], rawLabel: string) => {
    const label = normalizeClusterLabel(rawLabel);
    const key = `${subject}:${label}`;
    const existing = clusters.get(key);
    if (existing) return existing;
    const created: WeaknessClusterAccumulator = {
      subject,
      label,
      mistakeCount: 0,
      attempts: [],
      masteryAssessments: [],
    };
    clusters.set(key, created);
    return created;
  };

  for (const rawAttempt of input.practiceAttempts) {
    const parsed = practiceAttemptSchema.safeParse(rawAttempt);
    if (!parsed.success) continue;
    const attempt = parsed.data;
    if (attempt.attemptKind !== "first" || attempt.lookedUpDuringAttempt || !attempt.topic) continue;

    const effectiveQuestionCount = attempt.rawQuestionCount - attempt.duplicateQuestionCount;
    const timestampText = attempt.endedAt ?? attempt.startedAt;
    const timestamp = timestampText === undefined ? undefined : Date.parse(timestampText);
    getCluster(attempt.subject, attempt.topic).attempts.push({
      rate: attempt.correctCount / effectiveQuestionCount,
      timestamp: timestamp !== undefined && Number.isFinite(timestamp) ? timestamp : undefined,
    });
  }

  for (const mistake of input.mistakes) {
    if (mistake.status === "temporarily_mastered" || !isMistakeReadyForScheduling(mistake)) continue;
    getCluster(mistake.subject, mistake.knowledgeCluster).mistakeCount += 1;
  }

  for (const assessment of input.masteryAssessments ?? []) {
    const timestamp = assessment.createdAt === undefined ? undefined : Date.parse(assessment.createdAt);
    getCluster(assessment.subject, assessment.label).masteryAssessments.push({
      grade: assessment.masteryGrade,
      timestamp: timestamp !== undefined && Number.isFinite(timestamp) ? timestamp : undefined,
    });
  }

  return [...clusters.entries()]
    .map(([id, cluster]): WeaknessCluster => {
      const attempts = cluster.attempts;
      const timestamps = attempts.map((attempt) => attempt.timestamp);
      const hasReliableOrder = attempts.length > 0
        && timestamps.every((timestamp): timestamp is number => timestamp !== undefined)
        && new Set(timestamps).size === attempts.length;
      const orderedAttempts = hasReliableOrder
        ? [...attempts].sort((left, right) => left.timestamp! - right.timestamp!)
        : [];
      const latestRate = attempts.length === 1
        ? attempts[0]!.rate
        : hasReliableOrder
          ? orderedAttempts.at(-1)!.rate
          : undefined;
      const rateDropPercentagePoints = orderedAttempts.length >= 2
        ? Math.round(Math.max(
          0,
          (orderedAttempts.at(-2)!.rate - orderedAttempts.at(-1)!.rate) * 100,
        ) * 100) / 100
        : undefined;
      let consecutiveBelowSixtyCount = 0;
      for (let index = orderedAttempts.length - 1; index >= 0; index -= 1) {
        if (orderedAttempts[index]!.rate >= 0.6) break;
        consecutiveBelowSixtyCount += 1;
      }

      const assessments = cluster.masteryAssessments;
      const assessmentTimestamps = assessments.map((assessment) => assessment.timestamp);
      const hasReliableAssessmentOrder = assessments.length > 0
        && assessmentTimestamps.every((timestamp): timestamp is number => timestamp !== undefined)
        && new Set(assessmentTimestamps).size === assessments.length;
      const masteryGrade = assessments.length === 1
        ? assessments[0]!.grade
        : hasReliableAssessmentOrder
          ? [...assessments].sort((left, right) => left.timestamp! - right.timestamp!).at(-1)!.grade
          : undefined;

      return {
        id,
        subject: cluster.subject,
        label: cluster.label,
        latestRate,
        rateDropPercentagePoints,
        mistakeCount: cluster.mistakeCount,
        masteryGrade,
        consecutiveBelowSixtyCount,
      };
    })
    .sort((left, right) => left.subject.localeCompare(right.subject) || left.label.localeCompare(right.label, "zh-CN"));
}

function clusterSeverity(cluster: WeaknessCluster): number {
  if (
    cluster.masteryGrade === "A"
    && cluster.latestRate === undefined
    && cluster.mistakeCount === 0
  ) return 0;
  const rate = cluster.latestRate;
  const rateScore = rate === undefined ? 45 : rate < 0.5 ? 100 : rate < 0.7 ? 70 : rate < 0.8 ? 40 : 10;
  const masteryScore = { A: 0, B: 15, C: 35, D: 55 }[cluster.masteryGrade ?? "B"];
  const dropScore = Math.min((cluster.rateDropPercentagePoints ?? 0) * 2, 30);
  return rateScore + masteryScore + dropScore + Math.min(cluster.mistakeCount * 4, 28) + cluster.consecutiveBelowSixtyCount * 12;
}

export function buildNextDayAdjustment(input: NextDayAdjustmentInput): NextDayAdjustment {
  const overloadReasons: string[] = [];
  if (input.sleepHours !== undefined && input.sleepHours < 6.5) overloadReasons.push("睡眠不足 6.5 小时");
  if (input.fatigueLevel !== undefined && input.fatigueLevel >= 4) overloadReasons.push("主观疲劳达 4/5 或更高");
  if ((input.failedConcentrationBlockCount ?? 0) >= 2) overloadReasons.push("至少 2 个学习块无法集中 30 分钟");
  if ((input.rateDropPercentagePoints ?? 0) >= 8) overloadReasons.push("正确率较前次下降至少 8 个百分点");

  const topClusters = [...input.weaknessClusters]
    .filter((cluster) => clusterSeverity(cluster) > 0)
    .sort((left, right) => clusterSeverity(right) - clusterSeverity(left))
    .slice(0, 3);

  return {
    nextDate: input.nextDate,
    topClusters,
    firstSessionFocus: topClusters[0]?.label,
    overloadProtectionApplied: overloadReasons.length > 0,
    overloadReasons,
    reduceNewKnowledgeMinutes: overloadReasons.length > 0 ? 120 : 0,
    maxMacroBlockChanges: 2,
    mandatoryContinuity: ["法规闭卷回忆", "案例手写或案例骨架", "D1/D3/D7 错题回炉"],
    mandatoryReviewMistakeIds: [...new Set(input.mandatoryReviewMistakeIds)],
    clusterActions: topClusters.map((cluster, index) => ({
      clusterId: cluster.id,
      action:
        cluster.masteryGrade === "A"
          && cluster.latestRate === undefined
          && cluster.mistakeCount === 0
          ? "maintenance"
          : cluster.consecutiveBelowSixtyCount >= 2
          ? "rebuild_concept"
          : cluster.latestRate !== undefined && cluster.latestRate >= 0.8
            ? "maintenance"
            : index === 0
              ? "d1_review"
              : index === 1
                ? "d3_review"
                : "d7_review",
    })),
  };
}
